// mu — claim/release/resolveActorIdentity verbs.
//
// claimTask is the heart of mu's coordination protocol: an atomic
// CAS via a single SQL UPDATE, with two flavours:
//
//   "worker claim"  : --for <name> sets owner_id to that agent (FK to agents.id)
//   "anonymous claim": --self      keeps owner_id=NULL but flips status to
//                                  IN_PROGRESS and records the actor
//                                  in the ops log (ops.actor)
//
// resolveActorIdentity is the env-aware identity helper:
// $MU_AGENT_NAME > $USER > 'orchestrator'. Used by --self
// AND by the bare worker-claim path, so both flavours answer "who am
// I?" identically.

import { GUARD_ALREADY_OWNED } from "../capture.js";
import type { Db } from "../db.js";
import { say } from "../messages.js";
import { withOpContext } from "../op-context.js";
import { assertTaskRevision } from "../revision.js";
import {
  ClaimerNotRegisteredError,
  HumanTaskError,
  TaskAlreadyOwnedError,
  TaskInTriageError,
  TaskNotFoundError,
  TaskParkedError,
} from "./errors.js";
import { type AttributedEvidence, recordEvidenceNote } from "./lifecycle.js";
import { getTask } from "./queries.js";
import { DEFAULT_SUBSTATE, type TaskStatus } from "./status.js";

export interface ReleaseResult {
  /** The previous owner (null if the task was already unowned). */
  previousOwnerName: string | null;
  /** Status before the release. */
  previousStatus: TaskStatus;
  /** Status after the release. */
  status: TaskStatus;
  /** True iff owner OR status actually changed. */
  changed: boolean;
}

export interface ReleaseTaskOptions extends AttributedEvidence {
  /** `--if <rev>`: the task's revision from `mu task show`. A stale one
   *  throws `RevisionMismatchError` before any write. */
  ifRevision?: number;
  /** Workstream context for the task (v5: tasks.local_id is
   *  per-workstream unique). */
  workstream: string;
  /** Force `status = OPEN` regardless of the current status. Without
   *  this flag, `IN_PROGRESS` is also flipped to `OPEN` automatically
   *  (so a released task isn't left structurally stranded with
   *  `owner=NULL, status=IN_PROGRESS`); CLOSED is preserved.
   *  `--reopen` is the override for the rarer "un-close and hand
   *  back to the pool" workflow. */
  reopen?: boolean;
  /**
   * Rollback guard: release only while the task is STILL owned by this
   * registry agent id (`agents.id`) and not CLOSED, decided inside one
   * immediate transaction. A task another agent now owns, that was
   * released, or that was closed meanwhile is left alone (`changed: false`).
   */
  expectedOwnerId?: number;
}

/**
 * Release a task: clear `tasks.owner`.
 *
 * Status side-effects:
 *   - IN_PROGRESS → OPEN automatically (without it, the task is
 *     stranded: no owner to drive it forward, but `mu task next`
 *     skips it because it's not OPEN).
 *   - OPEN / CLOSED preserved.
 *   - `--reopen` forces OPEN regardless of current status — the
 *     escape hatch for un-closing a CLOSED owned task in one verb.
 *
 * Idempotent: releasing an already-unowned task with no `--reopen` and
 * no IN_PROGRESS status is a no-op (returns `changed: false`).
 * Throws TaskNotFoundError on missing.
 */
export function releaseTask(db: Db, localId: string, opts: ReleaseTaskOptions): ReleaseResult {
  return withOpContext(db, { intent: "task.release", actor: opts.author, group: "new" }, () =>
    releaseTaskImpl(db, localId, opts),
  );
}

function releaseTaskImpl(db: Db, localId: string, opts: ReleaseTaskOptions): ReleaseResult {
  if (opts.expectedOwnerId === undefined) return releaseTaskBody(db, localId, opts);
  // Check and write in ONE immediate transaction: nothing can re-own the task between.
  return db.transaction(() => releaseTaskBody(db, localId, opts)).immediate();
}

function releaseTaskBody(db: Db, localId: string, opts: ReleaseTaskOptions): ReleaseResult {
  const before = getTask(db, localId, opts.workstream);
  if (!before) throw new TaskNotFoundError(localId, opts.workstream);
  assertTaskRevision(db, before.workstreamName, localId, opts.ifRevision);
  if (
    opts.expectedOwnerId !== undefined &&
    (before.ownerId !== opts.expectedOwnerId || before.status === "CLOSED")
  ) {
    return {
      previousOwnerName: before.ownerName,
      previousStatus: before.status,
      status: before.status,
      changed: false,
    };
  }

  // Default: auto-flip IN_PROGRESS → OPEN so the released task isn't
  // left in the structurally weird owner=NULL/IN_PROGRESS limbo.
  // --reopen still wins for any status (including CLOSED).
  const newStatus: TaskStatus = opts.reopen
    ? "OPEN"
    : before.status === "IN_PROGRESS"
      ? "OPEN"
      : before.status;
  const ownerChanges = before.ownerName !== null;
  const statusChanges = newStatus !== before.status;

  if (!ownerChanges && !statusChanges) {
    return {
      previousOwnerName: before.ownerName,
      previousStatus: before.status,
      status: before.status,
      changed: false,
    };
  }

  // No pre-mutation snapshot: v9 dropped the `snapshots` table and
  // history is the ops log.

  db.prepare(
    `UPDATE tasks SET owner_id = NULL, status = ?, substate = ?, updated_at = ?
      WHERE local_id = ?
        AND workstream_id = (SELECT id FROM workstreams WHERE name = ?)`,
  ).run(
    newStatus,
    statusChanges
      ? before.kind === "human" && newStatus === "OPEN"
        ? "parked" // a human task is never ready
        : DEFAULT_SUBSTATE[newStatus]
      : before.substate,
    new Date().toISOString(),
    localId,
    before.workstreamName,
  );
  // No emitEvent: the UPDATE fired the capture trigger
  // (intent='task.release'), whose payload names owner_id and status —
  // the same facts the prose spelled out. Evidence goes to a note,
  // since the prose payload that used to carry it is gone.
  recordEvidenceNote(db, localId, before.workstreamName, "RELEASE", opts);
  return {
    previousOwnerName: before.ownerName,
    previousStatus: before.status,
    status: newStatus,
    changed: true,
  };
}

// ─── claimTask (verb) ──────────────────────────────────────────────────

export interface ClaimTaskOptions extends AttributedEvidence {
  /** `--if <rev>`: the task's revision from `mu task show`. A stale one
   *  throws `RevisionMismatchError` inside the claim transaction, before
   *  any write. */
  ifRevision?: number;
  /** Workstream context for both the task and the claiming agent.
   *  v5: agents.name and tasks.local_id are per-workstream unique;
   *  the task lookup AND the agent FK lookup scope to this
   *  workstream so a same-named task or worker elsewhere can't be
   *  silently picked. The CLI always passes this from the resolved
   *  -w or the injected worker scope. */
  workstream: string;
  /**
   * Override the agent name. If omitted, resolved from the ambient
   * environment via `resolveWorkerIdentity()`: `$MU_AGENT_NAME` only
   * (spawn injects it; pane titles are not identity).
   *
   * Mutually exclusive with `self: true`.
   */
  agentName?: string;
  /**
   * Workstream that the claimer agent lives in. When omitted, defaults
   * to `opts.workstream` (today's same-workstream behaviour). Set by
   * the CLI when `mu task claim X -w A --for B/worker-1` qualifies the
   * `--for` ref with a different workstream prefix
   * (`task_claim_for_cross_workstream`).
   *
   * Cross-workstream ownership is structurally allowed by the schema:
   * `tasks.owner_id` is an INTEGER FK to `agents.id` with no
   * workstream qualifier on the agent side. The per-workstream UNIQUE
   * on `agents(workstream_id, name)` is what previously made the
   * SDK's name → id lookup scope to one workstream; this option
   * widens that lookup to a different workstream when the operator
   * dispatches across a workstream boundary. The agent's own
   * workstream remains unchanged — only the task's `owner_id` points
   * out-of-workstream.
   */
  agentWorkstream?: string;
  /**
   * The registry id (`agents.id`) the caller created or validated for
   * `agentName`. Re-checked INSIDE the immediate claim transaction: when the
   * name now belongs to another incarnation (deleted and recreated), the
   * claim throws ClaimerNotRegisteredError and the replacement never owns
   * the task. Absent: the name is resolved as before.
   */
  expectedAgentId?: number;
  /**
   * Anonymous claim: write `owner_id = NULL` instead of resolving an agent
   * name and checking the FK. Use when the actor is the orchestrator
   * (or a script, or a human) doing direct work in a workstream they
   * aren't a registered worker in.
   *
   * The actor name is still recorded — it ends up in `ops.actor` of
   * the captured `task.claim` op — so provenance is preserved.
   * Just not in the FK column.
   *
   * Resolution order for the actor name (used as the log source):
   *   1. `actor` if explicitly passed.
   *   2. `$MU_AGENT_NAME`.
   *   3. `$USER`.
   *   4. The literal string 'orchestrator'.
   *
   * Mutually exclusive with `agentName` (the two are alternative
   * answers to "who's the actor for this claim?"). Passing both is a
   * usage error.
   */
  self?: boolean;
  /**
   * Override the actor name used for the log source when `self: true`.
   * Ignored when `self: false`. Useful when the orchestrator wants to
   * attribute the work to a meaningful name rather than the pane
   * title (e.g. "deploy-bot" rather than "pi-mu").
   */
  actor?: string;
  /**
   * Claim an OPEN/parked task anyway. Without it, a parked task throws
   * TaskParkedError before any write: parking means "keep out of the
   * scheduler", so overriding it must be explicit.
   */
  force?: boolean;
}

export interface ClaimResult {
  /** The agent now owning the task, or null when the claim was anonymous (--self). */
  ownerName: string | null;
  /** The actor recorded in the claim op — the agent name for a
   *  registered-worker claim, or the resolved actor for --self. */
  actorName: string;
  /** The previous owner (null if it was unowned). */
  previousOwnerName: string | null;
  /** The status BEFORE the claim; post-claim is IN_PROGRESS unless was CLOSED. */
  previousStatus: TaskStatus;
  /** The status AFTER the claim. */
  status: TaskStatus;
}

/**
 * Claim a task. Two modes:
 *
 *   Worker claim (default):
 *     Resolve an agent name from `opts.agentName`, else $MU_AGENT_NAME,
 *     else fails. The name
 *     MUST exist in the agents table (`tasks.owner_id` is an FK to
 *     `agents.id`). Sets `owner_id` to that agent. This is what mu-spawned
 *     workers do, and what `mu task claim --for <worker>` does for
 *     orchestrator dispatch.
 *
 *   Anonymous claim (--self):
 *     Skip the name -> agents FK lookup entirely. Sets `owner_id = NULL`.
 *     Records the actor in `ops.actor` instead. This is the
 *     orchestrator-doing-direct-work path — the actor is logged but
 *     not registered as a worker pane.
 *
 * Status side-effect: OPEN -> IN_PROGRESS; IN_PROGRESS / CLOSED unchanged.
 *
 * Concurrency: the worker-claim path uses a single-statement CAS UPDATE
 * with `WHERE owner IS NULL OR owner = ?` so two workers racing to
 * claim the same task can't both win. The anonymous path uses
 * `WHERE owner IS NULL` (anonymous claims don't 'own' the task in any
 * exclusive sense; if it's already owned by anyone, the anonymous claim
 * is a TaskAlreadyOwnedError just like a worker claim would be).
 */
export async function claimTask(
  db: Db,
  localId: string,
  opts: ClaimTaskOptions,
): Promise<ClaimResult> {
  // claimTask is async (it shells out to tmux to resolve the caller's
  // identity), and withOpContext is deliberately sync — see its doc
  // comment. So set the context around the SYNCHRONOUS mutation legs
  // inside, not around the whole async function.
  return claimTaskImpl(db, localId, opts);
}

async function claimTaskImpl(
  db: Db,
  localId: string,
  opts: ClaimTaskOptions,
): Promise<ClaimResult> {
  if (opts.self === true && opts.agentName !== undefined) {
    throw new Error(say("task.claim_self_and_for"));
  }

  if (opts.self === true) {
    return claimSelf(db, localId, opts);
  }

  // ── Worker claim path (registered agent owns the task) ──
  const agentName = opts.agentName ?? (await resolveWorkerIdentity());
  if (!agentName) {
    throw new Error(say("task.claim_no_agent"));
  }

  // Resolve the claiming agent to its surrogate id within the agent's
  // workstream — defaults to opts.workstream (today's same-ws path),
  // or opts.agentWorkstream when the CLI dispatched across a
  // workstream boundary via a qualified `--for <ws>/<name>` ref
  // (task_claim_for_cross_workstream).
  //
  // The schema permits cross-workstream owner_id assignment (FK to
  // agents.id only); the per-workstream UNIQUE on agents.name is the
  // only reason this SELECT was scoped narrowly before. Bare-name
  // dispatch keeps that scope to honour today's behaviour; qualified
  // dispatch widens it to the named workstream so the agent resolves
  // there.
  const claimerWorkstream = opts.agentWorkstream ?? opts.workstream;
  const claimerRow = db
    .prepare(
      `SELECT a.id AS id
         FROM agents a JOIN workstreams ws ON ws.id = a.workstream_id
        WHERE a.name = ? AND ws.name = ?`,
    )
    .get(agentName, claimerWorkstream) as { id: number } | undefined;
  if (!claimerRow) {
    const paneIdFromEnv = opts.agentName === undefined ? (process.env.TMUX_PANE ?? null) : null;
    throw new ClaimerNotRegisteredError(agentName, paneIdFromEnv);
  }

  return withOpContext(db, { intent: "task.claim", actor: agentName, group: "new" }, () =>
    db
      .transaction(() => {
        // Resolve the task within opts.workstream. This locks the
        // (workstream, local_id) pair for the rest of the transaction.
        const before = getTask(db, localId, opts.workstream);
        if (!before) throw new TaskNotFoundError(localId, opts.workstream);
        assertTaskRevision(db, before.workstreamName, localId, opts.ifRevision);
        assertNotParked(before, opts);
        // The name was resolved before this transaction began: re-read it now
        // and refuse an incarnation the caller did not mean.
        if (opts.expectedAgentId !== undefined) {
          const current = db
            .prepare(
              `SELECT a.id AS id FROM agents a JOIN workstreams ws ON ws.id = a.workstream_id
                WHERE a.name = ? AND ws.name = ?`,
            )
            .get(agentName, claimerWorkstream) as { id: number } | undefined;
          if (current?.id !== opts.expectedAgentId) {
            throw new ClaimerNotRegisteredError(agentName, null);
          }
        }

        const now = new Date().toISOString();
        let result: { changes: number };
        try {
          result = db
            .prepare(
              `UPDATE tasks
            SET owner_id = ?,
                status = CASE WHEN status = 'OPEN' THEN 'IN_PROGRESS' ELSE status END,
                -- SQLite evaluates every SET expression against the OLD
                -- row, so this CASE still sees the pre-claim status.
                substate = CASE WHEN status = 'OPEN' THEN 'active' ELSE substate END,
                updated_at = ?
          WHERE local_id = ?
            AND workstream_id = (SELECT id FROM workstreams WHERE name = ?)
            AND (owner_id IS NULL OR owner_id = ?)`,
            )
            .run(claimerRow.id, now, localId, opts.workstream, claimerRow.id);
        } catch (err) {
          // The DB-side CAS (`_cap_tasks_claim_cas`) backs the WHERE above.
          if (err instanceof Error && err.message === GUARD_ALREADY_OWNED) {
            throw new TaskAlreadyOwnedError(localId, before.ownerName ?? "<unknown>");
          }
          throw err;
        }

        if (result.changes === 0) {
          throw new TaskAlreadyOwnedError(localId, before.ownerName ?? "<unknown>");
        }

        const after = getTask(db, localId, opts.workstream);
        if (!after) throw new Error(`claimTask: row missing after update: ${localId}`);
        // The CLAIM note's author: the dispatcher when the CLI passed one
        // (`--for`), else the claiming agent.
        recordEvidenceNote(db, localId, opts.workstream, "CLAIM", {
          ...opts,
          author: opts.author ?? agentName,
        });
        // No emitEvent: the UPDATE fired the capture trigger under
        // intent='task.claim' with actor=agentName (withOpContext above
        // put it in op_ctx, and the trigger copies it into ops.actor).
        // The op payload carries the new owner_id; no prose
        // breadcrumb is written. `lastClaimActor` reads ops.actor.
        return {
          ownerName: agentName,
          actorName: agentName,
          previousOwnerName: before.ownerName,
          previousStatus: before.status,
          status: after.status,
        };
      })
      .immediate(),
  );
}

/** The parked-claim guard shared by the worker and --self paths. The
 *  UPDATE's `status = 'OPEN'` CASE already maps parked → active, so a
 *  forced claim needs nothing else. */
function assertNotParked(
  task: {
    name: string;
    kind: string;
    status: TaskStatus;
    substate: string;
    workstreamName: string;
  },
  opts: ClaimTaskOptions,
): void {
  if (task.kind === "human") throw new HumanTaskError(task.name, task.workstreamName, "claim");
  if (task.status === "OPEN" && task.substate === "parked" && opts.force !== true) {
    throw new TaskParkedError(task.name, task.workstreamName);
  }
  if (task.status === "OPEN" && task.substate === "triage" && opts.force !== true) {
    throw new TaskInTriageError(task.name, task.workstreamName);
  }
}

/**
 * Resolve the current actor's identity for attribution in task notes,
 * --self claims, and any other write that wants 'who did this?'.
 *
 * Resolution order:
 *   1. $MU_AGENT_NAME env var (set by mu spawnAgent on every managed
 *      pane; surfaced from the f3d4bdd commit). Authoritative when
 *      present — you're inside a mu-spawned worker, no ambiguity.
 *   2. $USER (when running outside a mu pane entirely).
 *   3. The literal 'orchestrator' as a last-resort default.
 *
 * The env var is set per-pane at spawn time and is unforgeable from
 * outside without explicit `--actor` override. Pane titles are not
 * identity: nothing reads or writes them.
 */
export async function resolveActorIdentity(): Promise<string> {
  const worker = await resolveWorkerIdentity();
  if (worker !== undefined) return worker;
  const user = process.env.USER;
  if (user !== undefined && user !== "") return user;
  return "orchestrator";
}

/**
 * The AGENT-identity half of the ladder, shared by `resolveActorIdentity`
 * and the bare worker-claim path. Returns undefined when the caller
 * isn't identifiable as a specific agent — the two callers disagree
 * about what to do then, which is why this stops short rather than
 * falling through to `$USER`:
 *
 *   - `resolveActorIdentity` continues to `$USER` / 'orchestrator',
 *     because an anonymous claim only needs an attribution string.
 *   - the worker-claim path THROWS, because `tasks.owner_id` is a real
 *     FK to `agents.id` and '$USER' is not an agent. Falling back there
 *     would turn a clear "who are you?" error into a confusing
 *     ClaimerNotRegisteredError naming the operator's unix login.
 *
 * Identity is `$MU_AGENT_NAME`, injected into every pane by spawnAgent.
 * It is backend-independent and unforgeable from outside the pane, and
 * the only source: pane titles are not identity.
 */
export async function resolveWorkerIdentity(): Promise<string | undefined> {
  const muAgent = process.env.MU_AGENT_NAME;
  return muAgent !== undefined && muAgent !== "" ? muAgent : undefined;
}

async function claimSelf(db: Db, localId: string, opts: ClaimTaskOptions): Promise<ClaimResult> {
  const actor =
    opts.actor !== undefined && opts.actor !== "" ? opts.actor : await resolveActorIdentity();
  return withOpContext(db, { intent: "task.claim", actor, group: "new" }, () =>
    db
      .transaction(() => {
        // Scope by the operator's workstream so a same-named task
        // elsewhere can't be self-claimed by accident.
        const before = getTask(db, localId, opts.workstream);
        if (!before) throw new TaskNotFoundError(localId, opts.workstream);
        assertTaskRevision(db, before.workstreamName, localId, opts.ifRevision);
        assertNotParked(before, opts);

        // Anonymous claim: owner stays NULL, status flips OPEN -> IN_PROGRESS.
        // Gate on `owner_id IS NULL` so an in-flight worker claim can't be
        // silently overwritten.
        const now = new Date().toISOString();
        const result = db
          .prepare(
            `UPDATE tasks
            SET status = CASE WHEN status = 'OPEN' THEN 'IN_PROGRESS' ELSE status END,
                substate = CASE WHEN status = 'OPEN' THEN 'active' ELSE substate END,
                updated_at = ?
          WHERE local_id = ?
            AND workstream_id = (SELECT id FROM workstreams WHERE name = ?)
            AND owner_id IS NULL`,
          )
          .run(now, localId, before.workstreamName);

        if (result.changes === 0) {
          // Task exists but is already owned (by someone). Mirror the
          // worker-path error so callers can pattern-match consistently.
          throw new TaskAlreadyOwnedError(localId, before.ownerName ?? "<unknown>");
        }

        const after = getTask(db, localId, before.workstreamName);
        if (!after) throw new Error(`claimTask: row missing after update: ${localId}`);
        recordEvidenceNote(db, localId, before.workstreamName, "CLAIM", {
          ...opts,
          author: opts.author ?? actor,
        });
        // No emitEvent. This is the interesting case: the `--self` path
        // leaves tasks.owner_id NULL deliberately, so the op PAYLOAD
        // cannot name the actor — but ops.actor can and does, because
        // withOpContext seeded op_ctx with it. That is precisely what
        // `lastClaimActor` needs, and reading a column beats
        // prefix-matching prose.
        return {
          ownerName: null,
          actorName: actor,
          previousOwnerName: before.ownerName,
          previousStatus: before.status,
          status: after.status,
        };
      })
      .immediate(),
  );
}
